import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, User, MapPin, Camera, Mic, Hammer, Megaphone, Loader2, FileText, Crosshair, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { LiveDebateTicker } from '../components/LiveDebateTicker';
import { Disclaimer } from '../components/Disclaimer';
import VideoEvidence from '../components/VideoEvidence';
import PublishToLedgerButton from '../components/PublishToLedgerButton';
import DraftingRoom from '../components/DraftingRoom';

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [meErr, setMeErr] = useState('');

  // Form state
  const [placeQuery, setPlaceQuery] = useState('');
  const [parcels, setParcels] = useState([]);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [photoName, setPhotoName] = useState('');

  // Generation state
  const [busy, setBusy] = useState(false);
  const [tickerEvents, setTickerEvents] = useState([]);
  const [debateActive, setDebateActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [generatedNotice, setGeneratedNotice] = useState(null);
  const [generatedIG, setGeneratedIG] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/me/').then(r => setMe(r.data)).catch(e => setMeErr(e.response?.data?.detail || e.message));
  }, []);

  const handleLogout = async () => { await signOut(); nav('/'); };

  const lookupParcel = async () => {
    setError(''); setParcels([]); setSelectedParcel(null);
    if (!placeQuery.trim()) { setError('Type a place name first'); return; }
    try {
      const r = await api.post('/parcel/lookup', { place_query: placeQuery });
      setParcels(r.data.parcels || []);
      if (r.data.parcels?.length) setSelectedParcel(r.data.parcels[0]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Lookup failed');
    }
  };

  const useGPS = () => {
    if (!navigator.geolocation) { setError('GPS not supported on this device'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await api.post('/parcel/lookup', { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setParcels(r.data.parcels || []);
        if (r.data.parcels?.length) setSelectedParcel(r.data.parcels[0]);
      } catch (e) { setError(e.response?.data?.detail || 'Lookup failed'); }
    }, () => setError('Could not get GPS fix'));
  };

  const startGenerate = (which) => {
    setError('');
    if (!selectedParcel) { setError('Pick a location first'); return; }
    if (which === 'notice' && userInput.trim().length < 20) { setError('Tell us what is happening on the land (at least 20 characters)'); return; }
    setPendingAction(which);
    setShowDisclaimer(true);
  };

  const runGenerate = async () => {
    setShowDisclaimer(false);
    setBusy(true); setError(''); setDebateActive(true);
    setGeneratedNotice(null); setGeneratedIG(null); setTickerEvents([]);
    setProgress(2);

    try {
      if (pendingAction === 'notice') {
        // Step 1: Queue the async War Room job - returns immediately with notice_id + debate_id
        const dispatch = await api.post('/notice/generate', {
          user_input: userInput,
          parcel: selectedParcel,
        });
        const { notice_id, debate_id } = dispatch.data;

        // Step 2: Poll BOTH the live debate ticker AND the notice status until terminal state.
        // Terminal = status is 'draft_ready' (v3), 'generated' (legacy), 'signed', or 'failed'.
        const TERMINAL = new Set(['draft_ready', 'generated', 'signed', 'failed']);
        const startedAt = Date.now();
        const MAX_MS = 10 * 60 * 1000; // 10 minutes hard ceiling

        let finalStatus = null;
        let finalPayload = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // Live ticker for the war-room agents
          try {
            const t = await api.get(`/notice/debate/${debate_id}/status`);
            setTickerEvents(t.data.events || []);
          } catch {}

          // Notice status (terminal-state detector)
          try {
            const s = await api.get(`/notice/status/${notice_id}`);
            const data = s.data || {};
            if (typeof data.progress === 'number') setProgress(data.progress);
            if (TERMINAL.has(data.status)) {
              finalStatus = data.status;
              finalPayload = data;
              break;
            }
          } catch (pollErr) {
            // 401/403/404 are unrecoverable - abort the polling loop
            const code = pollErr?.response?.status;
            if (code === 401 || code === 403 || code === 404) {
              setError(pollErr.response?.data?.detail || 'Polling failed');
              break;
            }
            // Network blip - swallow and keep polling
          }

          if (Date.now() - startedAt > MAX_MS) {
            setError('War Room timed out after 10 minutes. Check My Notices for the latest status.');
            break;
          }

          await new Promise(res => setTimeout(res, 2500));
        }

        if (finalStatus === 'generated' && finalPayload) {
          // Backwards compat (rows that pre-date v3 migration may still be 'generated'): keep behaviour.
          setGeneratedNotice({
            notice_id,
            debate_id,
            recipient_name: finalPayload.recipient_name,
            final_document: finalPayload.final_document || finalPayload.draft_text,
            violations: finalPayload.violations,
            standing_statement: finalPayload.standing_statement,
            mode: 'legacy',
          });
          setProgress(100);
        } else if (finalStatus === 'draft_ready' && finalPayload) {
          // New v3 flow: open the Drafting Room.
          setGeneratedNotice({
            notice_id,
            debate_id,
            recipient_name: finalPayload.recipient_name,
            mode: 'drafting',
          });
          setProgress(100);
        } else if (finalStatus === 'failed') {
          setError(finalPayload?.error || 'Generation failed inside the War Room. Please try again.');
        }
      } else if (pendingAction === 'ig') {
        const r = await api.post('/instagram/generate', {
          summary: userInput,
          parcel: selectedParcel,
        });
        setGeneratedIG(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Generation failed');
    } finally {
      setBusy(false); setDebateActive(false);
    }
  };

  const downloadPdf = async (noticeId) => {
    try {
      const token = (await import('../lib/supabaseClient')).supabase;
      const { data } = await token.auth.getSession();
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notice/${noticeId}/pdf`, {
        headers: { Authorization: `Bearer ${data.session.access_token}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `notice-${noticeId.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError('PDF download failed'); }
  };

  const sub = me?.profile?.subscription_active;

  return (
    <div className="min-h-screen bg-plai-bg flex flex-col" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard"><Logo size="sm" /></Link>
          <div className="flex items-center gap-4">
            <Link to="/notices" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white" data-testid="nav-notices">My Notices</Link>
            <Link to="/ledger" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white" data-testid="nav-ledger">Ledger</Link>
            <Link to="/subscription" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white" data-testid="nav-subscription">Subscription</Link>
            {me?.profile?.role === 'admin' && (
              <Link to="/admin" className="font-body text-xs tracking-[0.2em] uppercase text-plai-cta hover:text-white" data-testid="nav-admin">Admin</Link>
            )}
            <button onClick={handleLogout} className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="logout-btn">
              <LogOut className="h-3 w-3" strokeWidth={1.5} /> Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
        {meErr.includes('PGRST') || meErr.includes('schema') ? (
          <div className="glass iridescent-border p-6 mb-6" data-testid="setup-banner">
            <h3 className="font-headings text-xl uppercase mb-2 text-plai-cta">Database Setup Required</h3>
            <p className="font-body text-sm text-white/75">Run the SQL in <code className="bg-black px-2 py-1">/app/backend/database_setup.sql</code> in your Supabase SQL Editor, then refresh.</p>
          </div>
        ) : null}

        {!sub && me && (
          <div className="glass iridescent-border p-6 mb-6 flex items-center justify-between" data-testid="subscribe-banner">
            <div>
              <h3 className="font-headings text-xl uppercase mb-1">Subscribe To Generate</h3>
              <p className="font-body text-xs text-white/65 tracking-wide">Unlock the War Room for $9.99 a month. Cancel anytime.</p>
            </div>
            <Link to="/subscription" className="btn-primary-cta" data-testid="banner-subscribe-btn">Secure My Land</Link>
          </div>
        )}

        {/* Live Debate Ticker */}
        <div className="mb-6">
          <LiveDebateTicker events={tickerEvents} active={debateActive} />
          {debateActive && (
            <div className="mt-2" data-testid="heartbeat-bar">
              <div className="h-[3px] w-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-plai-cta transition-all duration-700 ease-out"
                  style={{ width: `${Math.min(100, Math.max(5, progress))}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50">
                  War Room Active - The Bombproof engine is still alive and researching
                </span>
                <span className="font-body text-[10px] tracking-[0.25em] uppercase text-plai-cta" data-testid="progress-pct">{Math.min(100, Math.max(5, progress))}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Single-scroll Command Center: bento grid */}
        <div className="grid lg:grid-cols-12 gap-4">
          {/* Jurisdiction */}
          <div className="lg:col-span-5 tactical-card p-6" data-testid="jurisdiction-panel">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-plai-teal" strokeWidth={1.5} />
              <h2 className="font-headings text-xl uppercase tracking-tight">Location</h2>
            </div>
            <div className="space-y-3">
              <input value={placeQuery} onChange={(e) => setPlaceQuery(e.target.value)}
                placeholder='e.g. "Big Bend South Rim" or "Larimer County, CO"'
                className="w-full px-3 py-3 text-sm" data-testid="place-query-input" />
              <div className="flex gap-2">
                <button onClick={lookupParcel} className="btn-ghost flex-1" data-testid="lookup-btn">
                  <Crosshair className="h-3 w-3 inline mr-1" strokeWidth={1.5} /> Find Parcel
                </button>
                <button onClick={useGPS} className="btn-ghost" data-testid="gps-btn">GPS Pulse</button>
              </div>
              {parcels.length > 0 && (
                <div className="space-y-2 mt-3" data-testid="parcels-list">
                  {parcels.map((p, i) => (
                    <button key={i} onClick={() => setSelectedParcel(p)}
                      className={`w-full text-left p-3 border ${selectedParcel?.parcel_id === p.parcel_id ? 'border-plai-teal bg-plai-teal/10' : 'border-white/10 hover:border-white/30'}`}
                      data-testid={`parcel-option-${i}`}>
                      <div className="font-body text-xs text-white/90">{p.jurisdiction}</div>
                      <div className="font-body text-[10px] text-white/50 mt-1">Owner: {p.owner} | Agency: {p.agency}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Evidence */}
          <div className="lg:col-span-3 tactical-card p-6" data-testid="evidence-panel">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="h-5 w-5 text-plai-purple" strokeWidth={1.5} />
              <h2 className="font-headings text-xl uppercase tracking-tight">Photo</h2>
            </div>
            <label className="block border border-dashed border-white/20 p-6 text-center cursor-pointer hover:border-plai-teal transition-colors" data-testid="photo-upload-label">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoName(e.target.files?.[0]?.name || '')} data-testid="photo-upload-input" />
              <Camera className="h-6 w-6 mx-auto text-white/40 mb-2" strokeWidth={1.5} />
              <p className="font-body text-xs text-white/60">Drop a photo</p>
              {photoName && <p className="font-body text-[10px] text-plai-teal mt-2">{photoName}</p>}
            </label>
            <p className="font-body text-[10px] tracking-wide text-white/40 mt-3">Optional. Use the Video panel below for moving clips.</p>
          </div>

          {/* Standing */}
          <div className="lg:col-span-4 tactical-card p-6" data-testid="standing-panel">
            <div className="flex items-center gap-2 mb-4">
              <Mic className="h-5 w-5 text-plai-gold" strokeWidth={1.5} />
              <h2 className="font-headings text-xl uppercase tracking-tight">Your Story</h2>
            </div>
            <textarea value={userInput} onChange={(e) => setUserInput(e.target.value)}
              rows={7} placeholder='Tell us what is happening. e.g. "I hike the Rim Trail at Big Bend and a wall is being built that will block the South Rim view."'
              className="w-full px-3 py-3 text-sm resize-none" data-testid="user-input-textarea" />
            <p className="font-body text-[10px] tracking-wide text-white/40 mt-2">{userInput.length} characters. Aim for at least 50.</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <button onClick={() => startGenerate('notice')} disabled={busy || !sub}
            className="iridescent-border bg-plai-cta text-black font-headings uppercase tracking-wide py-6 text-lg hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
            data-testid="generate-notice-btn">
            {busy && pendingAction === 'notice' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Hammer className="h-5 w-5" strokeWidth={2} />}
            Generate Notice of Intent
          </button>
          <button onClick={() => startGenerate('ig')} disabled={busy || !sub}
            className="iridescent-border bg-black text-white font-headings uppercase tracking-wide py-6 text-lg hover:bg-plai-purple/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
            data-testid="generate-ig-btn">
            {busy && pendingAction === 'ig' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Megaphone className="h-5 w-5" strokeWidth={1.5} />}
            Generate Instagram Post
          </button>
        </div>

        {error && <div className="mt-6 border border-red-500/40 bg-red-500/10 p-4 font-body text-sm text-red-300" data-testid="error-banner">{error}</div>}

        {/* Video Evidence (Phase 2 - Standalone Evidence Exhibit) */}
        {sub && (
          <div className="mt-6">
            <VideoEvidence noticeId={generatedNotice?.notice_id || null} />
          </div>
        )}

        {/* Results: Notice */}
        {generatedNotice && generatedNotice.mode === 'drafting' && (
          <div className="mt-8" data-testid="notice-result">
            <DraftingRoom
              noticeId={generatedNotice.notice_id}
              onSigned={() => nav('/notices')}
            />
            <div className="mt-3 flex justify-end">
              <PublishToLedgerButton noticeId={generatedNotice.notice_id} />
            </div>
          </div>
        )}

        {generatedNotice && generatedNotice.mode === 'legacy' && (
          <div className="mt-8 glass iridescent-border p-8" data-testid="notice-result">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-plai-cta" strokeWidth={1.5} />
                <h3 className="font-headings text-2xl uppercase">Draft Notice of Intent (legacy)</h3>
              </div>
              <div className="flex items-center gap-3">
                <PublishToLedgerButton noticeId={generatedNotice.notice_id} />
                <button onClick={() => downloadPdf(generatedNotice.notice_id)} className="btn-primary-cta" data-testid="download-pdf-btn">Download PDF</button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">Recipient</p>
                <p className="font-body text-sm text-white/90" data-testid="notice-recipient-name">{generatedNotice.recipient_name || 'Recipient pending'}</p>
              </div>
              <div>
                <p className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">Final Document</p>
                <pre className="font-body text-sm text-white/85 whitespace-pre-wrap leading-relaxed bg-black/40 p-4 max-h-96 overflow-y-auto" data-testid="notice-final-document">{generatedNotice.final_document}</pre>
              </div>
            </div>
          </div>
        )}

        {/* Results: IG */}
        {generatedIG && (
          <div className="mt-8 glass iridescent-border p-8" data-testid="ig-result">
            <div className="flex items-center gap-3 mb-6">
              <Megaphone className="h-6 w-6 text-plai-purple" strokeWidth={1.5} />
              <h3 className="font-headings text-2xl uppercase">Instagram Megaphone</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-black p-8 aspect-square flex items-center justify-center border border-white/10">
                <p className="font-headings text-3xl uppercase tracking-tighter text-center text-plai-cta" data-testid="ig-overlay">{generatedIG.overlay_text}</p>
              </div>
              <div>
                <p className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">Caption</p>
                <p className="font-body text-sm text-white/90 mb-4 whitespace-pre-wrap" data-testid="ig-caption">{generatedIG.caption}</p>
                <p className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">Hashtags ({generatedIG.hashtags?.length})</p>
                <p className="font-body text-xs text-plai-teal break-words" data-testid="ig-hashtags">{(generatedIG.hashtags || []).map(h => '#' + h.replace(/^#/, '')).join(' ')}</p>
                <p className="font-body text-[10px] tracking-wide text-white/40 mt-4">Live posting requires Meta App Review approval.</p>
              </div>
            </div>
          </div>
        )}

        {/* Usage */}
        {me?.usage && (
          <div className="mt-8" data-testid="usage-panel">
            <h3 className="font-headings text-lg uppercase tracking-tight mb-3">Monthly Usage</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(me.usage).map(([k, v]) => (
                <div key={k} className="tactical-card p-4" data-testid={`usage-${k}`}>
                  <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/50">{k.replace('_', ' ')}</p>
                  <p className="font-headings text-2xl mt-1">{v.used}<span className="text-white/40 text-base">/{v.cap}</span></p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showDisclaimer && (
        <Disclaimer onAccept={runGenerate} onCancel={() => { setShowDisclaimer(false); setPendingAction(null); }} />
      )}

      <Footer />
    </div>
  );
}
